import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import i18next from 'i18next'
import { useAuthStore } from '@/store/authStore'
import { queryClient } from '@/main'

// API base URL - can be configured via environment variable
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - add auth token and language header
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    config.headers['Accept-Language'] = i18next.language || 'en'
    return config
  },
  (error) => Promise.reject(error)
)

// Refresh token queue - prevents concurrent refresh calls racing against each other.
// With ROTATE_REFRESH_TOKENS=True on the backend, the first caller blacklists the
// refresh token; any subsequent caller using the same token gets a 401 and is logged out.
let isRefreshing = false
let refreshQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = []

function processQueue(error: unknown, token: string | null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error)
    else resolve(token!)
  })
  refreshQueue = []
}

// Response interceptor - handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      // Auth endpoints return 401 for legitimate reasons (wrong password, expired OTP etc.)
      // Don't attempt token refresh for these — just let the error propagate to the caller
      if (originalRequest.url?.includes('/auth/')) {
        return Promise.reject(error)
      }

      const refreshToken = useAuthStore.getState().refreshToken
      if (!refreshToken) {
        queryClient.clear()
        useAuthStore.getState().logout()
        window.location.href = '/login'
        return Promise.reject(error)
      }

      // If a refresh is already in flight, queue this request to retry once it resolves
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({
            resolve: (token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`
              resolve(apiClient(originalRequest))
            },
            reject,
          })
        })
      }

      isRefreshing = true

      try {
        const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
          refresh: refreshToken,
        })

        const { access, refresh } = response.data

        useAuthStore.getState().setTokens(access, refresh)
        processQueue(null, access)

        originalRequest.headers.Authorization = `Bearer ${access}`
        return apiClient(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        queryClient.clear()
        useAuthStore.getState().logout()
        window.location.href = '/login'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default apiClient
